import { useState, useEffect } from "react";
import { LuKey, LuCopy, LuCheck, LuEye, LuEyeOff, LuRefreshCw, LuTrash2, LuPlus, LuX, LuInfinity, LuSlidersHorizontal, LuBookOpen } from "react-icons/lu";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../hooks/useAuth";
import { API_URL } from "../services/api";
import DeveloperApiDocs from "../components/DeveloperApiDocs";

interface ApiKey {
    id: string;
    name: string;
    key: string;
    creditsRemaining: number;
    unlimited: boolean;
    createdAt: string;
    lastUsedAt?: string;
}

export default function DeveloperApi() {
    const { user, refreshUser } = useAuth();
    const { showToast } = useToast();

    const [activeTab, setActiveTab] = useState<"keys" | "docs">("keys");
    const [keys, setKeys] = useState<ApiKey[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showKeyMap, setShowKeyMap] = useState<Record<string, boolean>>({});
    const [copiedMap, setCopiedMap] = useState<Record<string, boolean>>({});
    
    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newKeyName, setNewKeyName] = useState("");
    const [allocateCredits, setAllocateCredits] = useState<number | "">("");
    const [isCreating, setIsCreating] = useState(false);
    const [createdKey, setCreatedKey] = useState<string | null>(null);

    // Limit modal state
    const [limitKey, setLimitKey] = useState<ApiKey | null>(null);
    const [limitAmount, setLimitAmount] = useState<number | "">("");
    const [isSavingLimit, setIsSavingLimit] = useState(false);

    const fetchKeys = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');

            const res = await fetch(`${API_URL}/api/keys`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await res.json();
            if (data.success) {
                setKeys(data.data);
            }
        } catch (err: any) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchKeys();
    }, []);

    const handleCopy = (keyId: string, keyValue: string) => {
        navigator.clipboard.writeText(keyValue);
        setCopiedMap(prev => ({ ...prev, [keyId]: true }));
        setTimeout(() => setCopiedMap(prev => ({ ...prev, [keyId]: false })), 2000);
        showToast({ title: "Copied!", message: "API Key copied to clipboard.", variant: "success", duration: 2000 });
    };

    const toggleShowKey = (keyId: string) => {
        setShowKeyMap(prev => ({ ...prev, [keyId]: !prev[keyId] }));
    };

    const handleCreateKey = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newKeyName.trim()) {
            showToast({ title: "Error", message: "Key name is required.", variant: "error" });
            return;
        }
        
        setIsCreating(true);
        try {
            const token = localStorage.getItem('token');

            const res = await fetch(`${API_URL}/api/keys`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: newKeyName,
                    allocateCredits: Number(allocateCredits) || 0
                })
            });

            const data = await res.json();
            if (data.success) {
                showToast({ title: "Success", message: "API Key created successfully.", variant: "success" });
                setCreatedKey(data.data.key);
                setIsModalOpen(false);
                setNewKeyName("");
                setAllocateCredits("");
                fetchKeys();
                refreshUser(); // Update main balance
            } else {
                throw new Error(data.message || 'Failed to create key');
            }
        } catch (err: any) {
            showToast({ title: "Error", message: err.message, variant: "error" });
        } finally {
            setIsCreating(false);
        }
    };

    const handleRevoke = async (keyId: string) => {
        if (!confirm("Are you sure? This key will be instantly invalidated and any remaining credits will be refunded to your main balance.")) {
            return;
        }
        
        try {
            const token = localStorage.getItem('token');

            const res = await fetch(`${API_URL}/api/keys/${keyId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await res.json();
            if (data.success) {
                showToast({ title: "Revoked", message: data.message || "API Key revoked successfully.", variant: "success" });
                fetchKeys();
                refreshUser(); // Update main balance
            } else {
                throw new Error(data.message || 'Failed to revoke key');
            }
        } catch (err: any) {
            showToast({ title: "Error", message: err.message, variant: "error" });
        }
    };

    const saveLimit = async (body: { unlimited: true } | { amount: number }) => {
        if (!limitKey) return;
        setIsSavingLimit(true);
        try {
            const token = localStorage.getItem('token');

            const res = await fetch(`${API_URL}/api/keys/${limitKey.id}/limit`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();
            if (data.success) {
                showToast({ title: "Updated", message: data.message || "Limit updated.", variant: "success" });
                setLimitKey(null);
                setLimitAmount("");
                fetchKeys();
                refreshUser();
            } else {
                throw new Error(data.message || 'Failed to update limit');
            }
        } catch (err: any) {
            showToast({ title: "Error", message: err.message, variant: "error" });
        } finally {
            setIsSavingLimit(false);
        }
    };

    return (
        <div className="p-4 md:p-8 bg-purple-50 min-h-screen">
            <div className="max-w-5xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Developer API</h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Manage multiple API keys and allocate credits to specific scripts or servers.
                        </p>
                    </div>
                    {activeTab === "keys" && (
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-sm rounded-xl hover:shadow-lg transition-all"
                        >
                            <LuPlus size={18} />
                            Create New Key
                        </button>
                    )}
                </div>

                {/* Tabs: Your Keys | Documentation */}
                <div className="inline-flex rounded-xl bg-gray-100 p-1 mb-6">
                    <button
                        onClick={() => setActiveTab("keys")}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === "keys" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                    >
                        <LuKey size={16} /> Your Keys
                    </button>
                    <button
                        onClick={() => setActiveTab("docs")}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === "docs" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                    >
                        <LuBookOpen size={16} /> Documentation
                    </button>
                </div>

                {activeTab === "docs" && <DeveloperApiDocs />}

                {activeTab === "keys" && (
                <>
                <div className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-purple-900/5 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase tracking-wider text-gray-500 font-bold">
                                    <th className="p-5 font-bold">Name</th>
                                    <th className="p-5 font-bold">Secret Key</th>
                                    <th className="p-5 font-bold">Limit / Balance</th>
                                    <th className="p-5 font-bold">Last Used</th>
                                    <th className="p-5 font-bold text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {isLoading && keys.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="p-8 text-center text-gray-500">
                                            <LuRefreshCw className="animate-spin inline-block mr-2" /> Loading keys...
                                        </td>
                                    </tr>
                                ) : keys.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="p-8 text-center text-gray-500">
                                            You have no API keys yet. Create one to get started!
                                        </td>
                                    </tr>
                                ) : (
                                    keys.map(key => (
                                        <tr key={key.id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="p-5 font-medium text-gray-900">{key.name}</td>
                                            <td className="p-5">
                                                <div className="flex items-center gap-2">
                                                    <code className="font-mono text-sm bg-gray-100 px-3 py-1.5 rounded-lg text-gray-700">
                                                        {showKeyMap[key.id] ? key.key : "igl_sk_live_" + "•".repeat(20)}
                                                    </code>
                                                    <button onClick={() => toggleShowKey(key.id)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded">
                                                        {showKeyMap[key.id] ? <LuEyeOff size={16} /> : <LuEye size={16} />}
                                                    </button>
                                                    <button onClick={() => handleCopy(key.id, key.key)} className="p-1.5 text-gray-400 hover:text-green-600 rounded">
                                                        {copiedMap[key.id] ? <LuCheck size={16} className="text-green-500" /> : <LuCopy size={16} />}
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="p-5">
                                                {key.unlimited ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <LuInfinity size={14} /> No limit (main balance)
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                        {Number(key.creditsRemaining).toFixed(2)} credits left
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-5 text-sm text-gray-500">
                                                {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : "Never"}
                                            </td>
                                            <td className="p-5 text-right">
                                                <button
                                                    onClick={() => { setLimitKey(key); setLimitAmount(key.unlimited ? "" : Number(key.creditsRemaining)); }}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors mr-1"
                                                >
                                                    <LuSlidersHorizontal size={16} />
                                                    {key.unlimited ? "Set limit" : "Edit limit"}
                                                </button>
                                                <button
                                                    onClick={() => handleRevoke(key.id)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <LuTrash2 size={16} />
                                                    Revoke
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-6 text-xs text-blue-600/80 bg-blue-100/50 p-4 rounded-xl flex items-start gap-3 border border-blue-200/50">
                    <LuKey className="shrink-0 mt-0.5 text-blue-500" size={16} />
                    <div>
                        <span className="font-bold block mb-1">Security & Billing Note</span>
                        <span>By default a key has <span className="font-semibold">no limit</span> and spends from your main balance. Setting a limit moves that many credits from your main balance into the key; the key stops when they run out. Removing a limit (or revoking the key) refunds any unspent credits back to your main balance. Never share your secret keys publicly.</span>
                    </div>
                </div>
                </>
                )}
            </div>

            {/* Create Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
                            <h3 className="text-lg font-bold text-gray-900">Create New API Key</h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <LuX size={24} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleCreateKey} className="p-6">
                            <div className="mb-5">
                                <label className="block text-sm font-bold text-gray-700 mb-1.5">Key Name</label>
                                <input
                                    type="text"
                                    required
                                    value={newKeyName}
                                    onChange={e => setNewKeyName(e.target.value)}
                                    placeholder="e.g. Production Server, Python Script"
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                />
                            </div>
                            
                            <div className="mb-6">
                                <div className="flex justify-between items-end mb-1.5">
                                    <label className="block text-sm font-bold text-gray-700">Spending limit <span className="font-normal text-gray-400">(optional)</span></label>
                                    <span className="text-xs text-gray-500 font-medium">
                                        Available: <span className="text-blue-600 font-bold">{Number(user?.creditsRemaining || 0).toFixed(2)}</span>
                                    </span>
                                </div>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={allocateCredits}
                                    onChange={e => setAllocateCredits(e.target.value === "" ? "" : Number(e.target.value))}
                                    placeholder="Leave empty for no limit"
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                />
                                <p className="text-xs text-gray-500 mt-2">
                                    Leave empty and the key has <span className="font-semibold">no limit</span> — it draws straight from your main balance.
                                    Enter an amount to cap it: that many credits move from your main balance into the key, and it stops once they run out. You can change this anytime.
                                </p>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-5 py-3 rounded-xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isCreating}
                                    className="flex-1 px-5 py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all disabled:opacity-50 flex items-center justify-center"
                                >
                                    {isCreating ? <LuRefreshCw className="animate-spin" /> : "Create Key"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Created Key Success Modal */}
            {createdKey && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
                            <h3 className="text-lg font-bold text-gray-900">API Key Created</h3>
                            <button onClick={() => setCreatedKey(null)} className="text-gray-400 hover:text-gray-600">
                                <LuX size={24} />
                            </button>
                        </div>
                        
                        <div className="p-6">
                            <div className="mb-4 text-sm text-gray-600">
                                Please copy your new API key now. For security reasons, <span className="font-bold text-red-600">you will not be able to see it again</span>.
                            </div>
                            
                            <div className="flex items-center gap-2 mb-6 bg-gray-50 p-3 rounded-xl border border-gray-200">
                                <code className="font-mono text-xs text-gray-800 break-all select-all flex-1">
                                    {createdKey}
                                </code>
                                <button
                                    onClick={() => handleCopy("new-key", createdKey)}
                                    className="p-2 text-gray-400 hover:text-green-600 bg-white rounded-lg border border-gray-200 shadow-sm transition-all flex items-center justify-center shrink-0"
                                >
                                    {copiedMap["new-key"] ? <LuCheck size={18} className="text-green-500" /> : <LuCopy size={18} />}
                                </button>
                            </div>
                            
                            <button
                                onClick={() => setCreatedKey(null)}
                                className="w-full px-5 py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all flex items-center justify-center"
                            >
                                Done & Copied
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Limit Modal */}
            {limitKey && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
                            <h3 className="text-lg font-bold text-gray-900">Spending limit — {limitKey.name}</h3>
                            <button onClick={() => setLimitKey(null)} className="text-gray-400 hover:text-gray-600">
                                <LuX size={24} />
                            </button>
                        </div>

                        <div className="p-6">
                            <p className="text-sm text-gray-600 mb-4">
                                {limitKey.unlimited
                                    ? "This key currently has no limit and draws from your main balance."
                                    : `This key is currently capped, with ${Number(limitKey.creditsRemaining).toFixed(2)} credits left.`}
                            </p>

                            <div className="mb-2 flex justify-between items-end">
                                <label className="block text-sm font-bold text-gray-700">Set limit to</label>
                                <span className="text-xs text-gray-500 font-medium">
                                    Main balance: <span className="text-blue-600 font-bold">{Number(user?.creditsRemaining || 0).toFixed(2)}</span>
                                </span>
                            </div>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={limitAmount}
                                onChange={e => setLimitAmount(e.target.value === "" ? "" : Number(e.target.value))}
                                placeholder="e.g. 20"
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                            />
                            <p className="text-xs text-gray-500 mt-2">
                                The difference between this and the key's current pool is moved to/from your main balance.
                            </p>

                            <div className="flex flex-col gap-3 pt-5">
                                <button
                                    disabled={isSavingLimit || limitAmount === "" || Number(limitAmount) <= 0}
                                    onClick={() => saveLimit({ amount: Number(limitAmount) })}
                                    className="w-full px-5 py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all disabled:opacity-50 flex items-center justify-center"
                                >
                                    {isSavingLimit ? <LuRefreshCw className="animate-spin" /> : "Save limit"}
                                </button>
                                {!limitKey.unlimited && (
                                    <button
                                        disabled={isSavingLimit}
                                        onClick={() => saveLimit({ unlimited: true })}
                                        className="w-full px-5 py-3 rounded-xl font-bold text-green-700 bg-green-50 hover:bg-green-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        <LuInfinity size={18} /> Remove limit (refund to main balance)
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
